import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${BACKEND_URL}/api`;

const BreakoutAlerts = ({ onSelectPair }) => {
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch alerts from backend
  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/breakout-alerts`);
      if (response.data.success) {
        setAlerts(response.data.alerts);
      }
    } catch (error) {
      console.error("Error fetching breakout alerts:", error);
    }
  };

  // Auto-refresh alerts every 30 seconds
  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Dismiss alert
  const handleDismiss = async (alertId) => {
    try {
      await axios.delete(`${API}/breakout-alerts/${alertId}`);
      setAlerts(alerts.filter(alert => alert.alert_id !== alertId));
      toast.success("Alert dismissed");
    } catch (error) {
      toast.error("Failed to dismiss alert");
    }
  };

  // Clear all alerts
  const handleClearAll = async () => {
    try {
      await axios.delete(`${API}/breakout-alerts/clear/all`);
      setAlerts([]);
      toast.success("All alerts cleared");
    } catch (error) {
      toast.error("Failed to clear alerts");
    }
  };

  // Calculate trade for this pair
  const handleCalculateTrade = (pair) => {
    setShowAlerts(false);
    onSelectPair(pair);
    toast.info(`Switched to ${pair} - Ready to calculate trade`);
  };

  // Manually trigger scan
  const handleManualScan = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/breakout-alerts/scan-now`);
      await fetchAlerts();
      toast.success("Breakout scan completed");
    } catch (error) {
      toast.error("Failed to scan for breakouts");
    } finally {
      setLoading(false);
    }
  };

  if (alerts.length === 0) {
    return null; // Don't show anything if no alerts
  }

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-4xl px-4">
      {/* Alert Banner */}
      <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-lg shadow-2xl border-2 border-yellow-600 p-4 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer flex-1"
            onClick={() => setShowAlerts(!showAlerts)}
          >
            <div className="text-3xl animate-pulse">⚠️</div>
            <div>
              <h3 className="text-white font-bold text-lg">
                Breakout Alert{alerts.length > 1 ? 's' : ''} Detected!
              </h3>
              <p className="text-white text-sm">
                {alerts.length} potential breakout{alerts.length > 1 ? 's' : ''} - Click to view
              </p>
            </div>
            <div className="ml-auto bg-white text-red-600 font-bold rounded-full w-8 h-8 flex items-center justify-center">
              {alerts.length}
            </div>
          </div>
          
          <div className="flex gap-2 ml-4">
            <button
              onClick={handleManualScan}
              disabled={loading}
              className="bg-white hover:bg-gray-100 text-gray-800 px-3 py-2 rounded-lg font-semibold text-sm transition-all disabled:opacity-50"
            >
              {loading ? "Scanning..." : "🔄 Scan Now"}
            </button>
            <button
              onClick={handleClearAll}
              className="bg-white hover:bg-gray-100 text-gray-800 px-3 py-2 rounded-lg font-semibold text-sm transition-all"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Alerts Dropdown */}
        {showAlerts && (
          <div className="mt-4 bg-white rounded-lg shadow-xl max-h-96 overflow-y-auto">
            {alerts.map((alert) => (
              <div
                key={alert.alert_id}
                className="p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Pair and Direction */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-xl text-gray-900">
                        {alert.pair}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-bold ${
                          alert.direction === "BUY"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {alert.direction}
                      </span>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-bold">
                        {alert.confidence}% Confidence
                      </span>
                    </div>

                    {/* Conditions Met */}
                    <div className="mb-2">
                      <p className="text-sm text-gray-600 font-medium">
                        ✅ {alert.conditions_met}/5 Conditions Met
                      </p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {alert.conditions.consolidation?.is_consolidating && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            📊 Consolidating
                          </span>
                        )}
                        {alert.conditions.volatility?.is_contracting && (
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded">
                            📉 Low Volatility
                          </span>
                        )}
                        {alert.conditions.key_level?.near_level && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                            🎯 Near {alert.conditions.key_level.level_type}
                          </span>
                        )}
                        {alert.conditions.session?.in_overlap && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                            ⏰ High Liquidity Session
                          </span>
                        )}
                        {alert.conditions.market_strength === "strong" && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                            💪 Strong Trend
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Trade Recommendations */}
                    <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="text-gray-500 text-xs">Entry</p>
                        <p className="font-bold text-gray-900">
                          {alert.recommended_entry}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="text-gray-500 text-xs">Stop Loss</p>
                        <p className="font-bold text-red-600">
                          {alert.recommended_sl}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="text-gray-500 text-xs">Take Profit</p>
                        <p className="font-bold text-green-600">
                          {alert.recommended_tp}
                        </p>
                      </div>
                    </div>

                    {/* Timestamp */}
                    <p className="text-xs text-gray-400 mt-2">
                      Detected: {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => handleCalculateTrade(alert.pair)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all whitespace-nowrap"
                    >
                      📊 Calculate Trade
                    </button>
                    <button
                      onClick={() => handleDismiss(alert.alert_id)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold text-sm transition-all"
                    >
                      ✕ Dismiss
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BreakoutAlerts;
